import React, { useState } from 'react';
import { MachineData } from '../types/types';
import { Settings, Home, Wrench, Droplets, Brush, Wind, Cylinder, Sun, Stamp, ChevronLeft, ChevronRight, PaintBucketIcon, PrinterIcon } from 'lucide-react';
interface SidebarProps {
  machines: MachineData[];
  selectedMachine: string;
  setSelectedMachine: (id: string) => void;
  showMainDashboard: boolean;
  setShowMainDashboard: (show: boolean) => void;
}
const getMachineIcon = (type: string) => {
  switch (type) {
    case 'ausbeulanlage':
      return <Wrench size={20} data-id="element-765" />;
    case 'waschanlage':
      return <Droplets size={20} data-id="element-766" />;
    case 'roboterarm':
      return <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/exJr8iTrUaoxf2Ya9i5G1K/robotic-arm.svg" alt="Roboter" width={20} height={20} className="brightness-0 invert" data-id="element-767" />;
    case 'faesserBuerste':
      return <Brush size={20} data-id="element-768" />;
    case 'sandstrahlmaschine':
      return <Wind size={20} data-id="element-769" />;
    case 'buersteMaschine':
      return <Brush size={20} data-id="element-770" />;
    case 'dichtpruefungsmaschine':
      return <Cylinder size={20} data-id="element-771" />;
    case 'trocknung':
      return <Sun size={20} data-id="element-772" />;
    case 'signiermaschine':
      return <Stamp size={20} data-id="element-773" />;
    case 'lackieranlage':
      return <PaintBucketIcon size={20} data-id="element-774" />;
    case 'siebdruckanlage':
      return <PrinterIcon size={20} data-id="element-775" />;
    default:
      return <Wrench size={20} data-id="element-776" />;
  }
};
export const Sidebar: React.FC<SidebarProps> = ({
  machines,
  selectedMachine,
  setSelectedMachine,
  showMainDashboard,
  setShowMainDashboard
}) => {
  const [collapsed, setCollapsed] = useState(false);
  // Custom order for machines in the sidebar
  const getOrderedMachines = () => {
    // Create a copy of the machines array
    const orderedMachines = [...machines];
    // Find indices of Roboter and Waschanlage
    const robotIndex = orderedMachines.findIndex(m => m.type === 'roboterarm');
    const waschIndex = orderedMachines.findIndex(m => m.type === 'waschanlage');
    // Swap Roboter and Waschanlage if both exist
    if (robotIndex !== -1 && waschIndex !== -1) {
      const temp = orderedMachines[robotIndex];
      orderedMachines[robotIndex] = orderedMachines[waschIndex];
      orderedMachines[waschIndex] = temp;
    }
    // Reorder to place Lackieranlage after Dichtprüfungsanlage
    const dichtIndex = orderedMachines.findIndex(m => m.type === 'dichtpruefungsmaschine');
    const lackierIndex = orderedMachines.findIndex(m => m.type === 'lackieranlage');
    if (dichtIndex !== -1 && lackierIndex !== -1 && lackierIndex !== dichtIndex + 1) {
      // Remove Lackieranlage from its current position
      const lackieranlage = orderedMachines.splice(lackierIndex, 1)[0];
      // Insert it after Dichtprüfungsanlage
      orderedMachines.splice(dichtIndex + 1, 0, lackieranlage);
    }
    return orderedMachines;
  };
  return <div className={`${collapsed ? 'w-16' : 'w-64'} h-full bg-gray-800 border-r border-gray-700 flex flex-col relative transition-all duration-300 ease-in-out`} data-id="element-777">
      <div className="p-4 border-b border-gray-700 flex items-center justify-between" data-id="element-778">
        <div className={`flex items-center ${collapsed ? 'justify-center w-full' : 'flex-1 justify-center'}`} data-id="element-779">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6vyZXQEzJErmrXMM1DLCYq/fass-braun-logo.svg" alt="Fass Braun Logo" className={`${collapsed ? 'w-10 h-10' : 'w-auto h-12'}`} data-id="element-780" />
        </div>
        <button onClick={() => setCollapsed(!collapsed)} className="p-1 rounded-full hover:bg-gray-700 transition-colors absolute right-2" data-id="element-781">
          {collapsed ? <ChevronRight size={16} data-id="element-782" /> : <ChevronLeft size={16} data-id="element-783" />}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto py-4 overflow-x-hidden" data-id="element-784">
        {!collapsed && <div className="px-4 mb-2 text-sm font-medium text-gray-400" data-id="element-785">
            Übersicht
          </div>}
        <button className={`w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3 px-4 py-2 rounded-lg ${collapsed ? 'mx-0' : 'mx-2'} mb-1 ${showMainDashboard ? 'bg-gray-700 text-blue-400' : 'text-gray-300 hover:bg-gray-700'}`} onClick={() => setShowMainDashboard(true)} title="Hauptdashboard" data-id="element-786">
          <Home size={20} data-id="element-787" />
          {!collapsed && <span data-id="element-788">Hauptdashboard</span>}
        </button>
        {!collapsed && <div className="px-4 mb-2 mt-6 text-sm font-medium text-gray-400" data-id="element-789">
            Maschinen
          </div>}
        {collapsed && <div className="my-6 border-t border-gray-700" data-id="element-790"></div>}
        <div className={`space-y-1 ${collapsed ? 'px-0' : 'px-2'}`} data-id="element-791">
          {getOrderedMachines().map(machine => {
          const machineName = machine.type === 'roboterarm' ? 'Roboter' : machine.type === 'sandstrahlmaschine' ? 'Sandstrahlanlage' : machine.type === 'buersteMaschine' ? 'Bürstanlage' : machine.type === 'schleifanlage' || machine.type === 'schleifmaschine' ? 'Bürstanlage' : machine.type === 'dichtpruefungsmaschine' ? 'Dichtprüfungsanlage' : machine.type === 'trocknung' ? 'Trocknungsanlage' : machine.type === 'signiermaschine' ? 'Signieranlage' : machine.type === 'faesserBuerste' ? 'Etikettenschleifanlage' : machine.type === 'lackieranlage' ? 'Lackieranlage' : machine.type === 'siebdruckanlage' ? 'Siebdruckanlage' : machine.name;
          return <button key={machine.id} className={`w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3 px-4 py-2 rounded-lg transition-colors ${selectedMachine === machine.id && !showMainDashboard ? 'bg-gray-700 text-blue-400' : 'text-gray-300 hover:bg-gray-700'}`} onClick={() => {
            setSelectedMachine(machine.id);
            setShowMainDashboard(false);
          }} title={machineName} data-id="element-792">
                {getMachineIcon(machine.type)}
                {!collapsed && <span className="truncate" data-id="element-793">{machineName}</span>}
              </button>;
        })}
        </div>
      </div>
      <div className={`p-4 border-t border-gray-700 ${collapsed ? 'flex justify-center' : ''}`} data-id="element-794">
        <button className={`${collapsed ? 'w-auto' : 'w-full'} flex items-center ${collapsed ? 'justify-center' : ''} gap-3 px-4 py-2 text-gray-300 hover:bg-gray-700 rounded-lg`} title="Einstellungen" data-id="element-795">
          <Settings size={20} data-id="element-796" />
          {!collapsed && <span data-id="element-797">Einstellungen</span>}
        </button>
      </div>
    </div>;
};